import PriorityQueue from './priority-queue'
import TaskPool from './task-pool'
import { Log } from './logs'
import { uniqueString } from '../utils/string'

const _batchSize = uniqueString('LogManager_batchSize')
const _batchDelay = uniqueString('LogManager_batchDelay')
const _batchMeta = uniqueString('LogManager_batchMeta')
const _instance = uniqueString('LogManager_instance')
const _taskPool = uniqueString('LogManager_taskPool')
const _providers = uniqueString('LogManager_providers')
const _queue = uniqueString('LogManager_queue')
const _manualReleaseTimeout = uniqueString('LogManager_manualReleaseTimeout')
const _maxLogs = uniqueString('LogManager_maxLogs')
const _resolveDelay = uniqueString('LogManager_resolveDelay')
const _running = uniqueString('LogManager_running')

export default class LogManager {
  constructor(config = {}) {
    this[_taskPool] = new TaskPool({
      poolSize: 5,
    })

    this[_batchSize] = config.batchSize || 30
    this[_batchDelay] = config.batchDelay || 5000
    this[_batchMeta] = config.batchMeta
    this[_providers] = {}
    this[_queue] = new PriorityQueue()
    this[_manualReleaseTimeout] = null
    this[_maxLogs] = config.maxLogs || 1000
    this[_resolveDelay] = config.resolveDelay || 500
    this[_running] = false
  }

  get running() {
    return this[_running]
  }

  log(data, priority, provider) {
    if (this[_queue].size >= this[_maxLogs]) return

    const log = data instanceof Log ? data : new Log(data)

    if (provider) log.provider = provider

    this[_queue].enqueue(log, priority)
    this._start()
  }

  release(meta) {
    if (this[_manualReleaseTimeout]) return

    this[_manualReleaseTimeout] = setTimeout(() => {
      this[_manualReleaseTimeout] = clearTimeout(this[_manualReleaseTimeout])
    }, this[_batchDelay])

    this._send(meta)
  }

  _start() {
    this[_running] = true

    setTimeout(this._send.bind(this), this[_batchDelay])
  }

  _send(options = {}) {
    if (!this[_running] || this[_taskPool].full) return

    const batchSize = this[_batchSize]
    const totalProviders = this[_taskPool].max - this[_taskPool].size
    const providers = LogManager.parseProviders(this[_queue], batchSize, totalProviders)
    const manual = !!options.manual

    for (const [provider, state] of providers) {
      const batchCount = Math.ceil(state.data.length / batchSize)

      for (let i = 0; i < batchCount; ++i) {
        const index = i * batchSize
        const lastIndex = index + batchSize
        const logs = state.data.slice(index, lastIndex)

        this[_taskPool].add(() => {
          return provider.onBatch(logs, { manual }).then(() => {
            if (this[_queue].size && !manual) {
              setTimeout(this._send.bind(this), 1000)
            } else {
              this[_running] = false
            }
          })
        })
      }
    }
  }

  _stop() {
    if (!this[_running]) return

    this[_running] = false
  }

  static parseProviders(queue, count, max, p = new Map()) {
    const diff = count > queue.size ? count - queue.size : count

    for (let i = 0; i < diff; ++i) {
      const next = queue.front

      if (!next) break
      if (!p.has(next.provider) && p.size <= max) {
        p.set(next.provider, { data: [] })
      }

      p.get(next.provider).data.push(queue.dequeue())
    }

    if (!queue.empty && p.size < max) {
      this.parseProviders(queue, count, --max, p)
    }

    return p
  }

  static get instance() {
    if (!this[_instance]) this[_instance] = new LogManager()

    return this[_instance]
  }
}
